import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { LocalStorage } from '../local-storage/local-storage.service';
import { environment } from '../../../../environments/environment';
import { Api } from '../api/api';
import { User } from '../../models/interfaces/user.interface';
import { UserPayload } from '../../types/payloads/user.payload';
import { userMock } from '../../mocks/interfaces/user.mock';
import { isFakeBackendEnabled } from '../../utils/fake-backend.util';
import { TokenDto } from '../auth/models/token.dto';
import { mergeIamUser } from '../../utils/jwt-claims.util';

@Injectable({
  providedIn: 'root',
})
export class MeService {

  constructor(
    private readonly localStorage: LocalStorage,
  ) { }

  private readonly api: Api = new Api();

  private readonly _me: WritableSignal<User | null> = signal(null);

  public async loadMe(): Promise<User | null> {
    if (isFakeBackendEnabled()) {
      return this.loadMeMocked();
    }

    const response = await this.api.getAsync<User>(
      environment.routes.users.me
    );

    return this.hydrateUserRoles(response);
  }

  public async loadMeMocked(): Promise<User> {
    return this.getMe() ?? userMock;
  }

  public getMe(): User | null {
    return this.localStorage.getItem<User>(
      environment.keys.user
    );
  }

  public async storageMe(): Promise<void> {
    this.me = await this.loadMe();
  }

  public async storageMeMocked(): Promise<void> {
    this.me = await this.loadMeMocked();
  }

  public async updateProfile(payload: UserPayload): Promise<User | null> {
    if (isFakeBackendEnabled()) {
      return this.updateProfileMocked(payload);
    }

    const me = this.getMe();

    if (!me) {
      return null;
    }

    const response = await this.api.putAsync<User>(environment.routes.users.one(me.id), {
      ...payload,
      imageUrl: payload.imageUrl ?? me.imageUrl,
    });

    if (!response) {
      return null;
    }

    const hydrated = this.hydrateUserRoles(response, me);
    this.me = hydrated;
    return hydrated;
  }

  public async updateProfileMocked(payload: UserPayload): Promise<User | null> {
    const current = this.getMe() ?? userMock;

    const updated: User = {
      ...current,
      name: payload.name ?? current.name,
      email: payload.email ?? current.email,
      imageUrl: payload.imageUrl ?? current.imageUrl,
    };

    this.me = updated;
    return updated;
  }

  public get me(): Signal<User | null> {
    return this._me;
  }

  public set me(user: User | null) {
    this.localStorage.setItem(environment.keys.user, user);
    this._me.set(user);
  }

  private hydrateUserRoles(user: User | null, fallback?: User | null): User | null {
    const token = this.localStorage.getItem<TokenDto>(environment.keys.token);
    const hydrated = mergeIamUser(user, token?.token);

    if (!hydrated) {
      return null;
    }

    const roles = hydrated.roles?.length ? hydrated.roles : fallback?.roles;
    const groupIds = hydrated.groupIds?.length ? hydrated.groupIds : fallback?.groupIds;
    const context = hydrated.context ?? fallback?.context;

    return {
      ...hydrated,
      ...(roles?.length ? { roles } : {}),
      ...(groupIds?.length ? { groupIds } : {}),
      ...(context ? { context } : {}),
    };
  }
}
